// src/ui/featureIndex.js — every capability Allo actually ships, in one place.
//
// 🟥 EVERY LINE HERE WAS READ OUT OF THE CODEBASE, NOT REMEMBERED. The source
//    was the app's 1,550-string English locale file plus the screens, services
//    and server routes that back each string. A landing page is the one document
//    where an enthusiastic guess becomes a lie a customer can catch, so the rule
//    while writing this was: if there is no string and no file, it does not go
//    on the page.
//
// 🟥 THINGS DELIBERATELY ABSENT, so nobody re-adds them from memory:
//      screen sharing            — no getDisplayMedia anywhere, in either client
//      stories / 24-hour status  — the "status" strings are check-ins on a Vibe
//      communities               — no container-of-groups concept exists
//      editing a sent message    — real for Vibes COMMENTS only, not for chats
//      view-once media           — Private Media is a biometric cover, not a
//                                  one-time view; disappearing is per-chat
//      broadcast lists, payments, message scheduling, a GIF picker,
//      user-uploaded sticker packs, "invite friends" (its own string says
//      "coming soon"), in-app help (same), a stated group-size limit
//    Several of these are the features a reader would ASSUME a messenger has.
//    That is exactly why they are listed: the absence has to be deliberate and
//    written down, or the next person to touch this file will helpfully put
//    "screen sharing" back.
//
// 🟢 Counts are exact. 8 chat themes, 10 app icons and the rest were
//    counted from the arrays that define them, so they can be stated as numbers
//    rather than hedged with "lots of".

export const FEATURE_GROUPS = [
  {
    id: 'messaging',
    title: 'Messaging',
    blurb: 'The part you use a hundred times a day.',
    items: [
      ['One-to-one and group chats', 'Text anyone on Allo, one person or a whole group.'],
      ['Note to Self', 'A private chat with yourself for notes, links and files.'],
      ['Replies', 'Quote any message and answer it directly.'],
      ['Forwarding', 'Pass a message on, labelled so nobody mistakes it for your own words.'],
      ['Emoji reactions', 'React without adding a message to the thread.'],
      ['Select several at once', 'Copy, forward, star or delete a batch of messages together.'],
      ['Starred messages', 'Keep the ones that matter in their own screen.'],
      ['Pin and archive', 'Hold a chat at the top, or move a quiet one out of the way.'],
      ['Search inside a chat', 'Find a message in one conversation.'],
      ['Search everything', 'One search across every conversation and person.'],
      ['Typing indicators', 'And not just dots — recording a voice note shows a mic instead.'],
      ['Delivery and read ticks', 'Sent, delivered, read — with colour when it has been read.'],
      ['Message info', 'Exactly when it arrived and when it was read, per member in a group.'],
      ['Online and last seen', 'If you have chosen to share it.'],
      ['Polls', 'Ask a question in a chat; results update live as people vote.'],
      ['Live location', 'Share where you are once, or continuously for 15 minutes, 2 hours or 8 hours.'],
      ['Contact cards', 'Send someone a contact they can save in one tap.'],
      ['Flight cards', 'Paste a flight number and the chat tracks the flight.'],
      ['Link previews', 'A shared link unfurls into a card with its title and image.'],
      ['In-app browser', 'Links open inside Allo instead of throwing you into Safari.'],
      ['Stickers and emoji', 'A built-in picker and three sticker packs.'],
      ['Voice messages', 'Hold to record, slide to cancel.'],
      ['Text styling', 'Format what you type.'],
      ['Drafts that survive', 'An unfinished message is still there when you come back.'],
      ['Offline queue', 'Write with no signal; it waits and tells you it is waiting.'],
      ['Delete for everyone', 'Take a message back; a marker shows it was removed.'],
      ['Clear or delete a chat', 'Empty a conversation or remove it entirely.'],
      ['Unread tools', 'Filter to unread, mark everything read, or read an unread summary.'],
      ['List or tile view', 'A classic list, or a two-column grid.'],
      ['Swipe actions', 'Swipe a row for the thing you were about to tap three times for.'],
      ['Chat lists', 'Group conversations into lists of your own.'],
      ['First-contact card', 'When a stranger messages first, Allo shows what it knows — their country, whether they are in your contacts, groups you share, and how long they have been on Allo.'],
      ['Screenshot notice', 'The conversation records when it is screenshotted.'],
      ['Home-screen shortcut', 'Pin one conversation to your phone’s home screen.'],
      ['Share into Allo', 'Send a photo, file or link from any other app straight into a chat.'],
    ],
  },
  {
    id: 'media',
    title: 'Media, documents and files',
    blurb: 'Everything that is not text.',
    items: [
      ['Built-in gallery picker', 'Recents, Today and Yesterday grouping, and it understands iCloud photos that are not downloaded yet.'],
      ['Limited photo access', 'If you only granted access to some photos, Allo says so and offers to add more.'],
      ['Camera in the chat', 'Shoot, retake, caption, send.'],
      ['Five photo filters', 'Normal, Warm, Cool, Vintage and Dim.'],
      ['Video trimming', 'Cut a video down before it goes.'],
      ['Captions', 'Words attached to the photo, not floating underneath it.'],
      ['Up to ten at once', 'Send a batch in one action.'],
      ['Long videos sent as files', 'Over five minutes, Allo sends the file so the quality survives.'],
      ['Adaptive quality', 'On a weak connection a video is sent in SD so it actually arrives.'],
      ['Any document', 'With a typed icon, a thumbnail and a viewer that opens it in place.'],
      ['Media, links and docs hub', 'Every photo, video, file, link, poll and location from a chat, in browsable tabs.'],
      ['Downloads you can control', 'Progress, cancel, resume.'],
      ['Save to your gallery', 'Keep a received photo or video on your phone.'],
      ['Uploads keep going', 'Leave the chat; the upload does not stop.'],
      ['Auto-download rules', 'Separate settings for mobile data, Wi-Fi and roaming.'],
      ['Storage manager', 'See what Allo is using, sort by size or date, and clear the big things.'],
      ['Free up space', 'Delete downloaded media from the phone without losing the message.'],
    ],
  },
  {
    id: 'calls',
    title: 'Voice and video calls',
    blurb: 'Built on WebRTC, with an unusual amount of attention paid to bad networks.',
    items: [
      ['Voice and video calls', 'Call anyone on Allo, from the phone or the browser.'],
      ['Turn a voice call into video', 'Ask mid-call; the other side accepts or declines.'],
      ['Rings like a real call', 'Full-screen on the lock screen, through the system call interface.'],
      ['Answer from the notification', 'Answer or decline without opening the app.'],
      ['Audio output switching', 'Earpiece, speaker or Bluetooth, mid-call.'],
      ['Picture-in-picture', 'Shrink the call into a floating window and keep using your phone.'],
      ['Minimised call bar', 'A slim bar keeps the call alive while you browse Allo.'],
      ['Proximity handling', 'The screen sleeps when the phone is at your ear.'],
      ['Video effects', 'Adjust your camera before and during the call.'],
      ['HD badge and live stats', 'Tap it for the real connection statistics.'],
      ['It tells you whose network is failing', 'Not a frozen picture and a shrug — Allo says which side is struggling and what it is doing about it. Forty separate messages exist for this one job.'],
      ['Pre-call network check', 'A warning before dialling if the connection cannot carry it, and an offer to go voice-only instead.'],
      ['Self-healing calls', 'A dropped connection tries to restart itself rather than ending the call.'],
      ['Call history', 'Filterable by missed, incoming and outgoing, with bulk delete.'],
      ['Call back', 'One tap, as voice or as video.'],
      ['Find someone to call', 'By name, @username or phone number.'],
      ['Chat during a call', 'Messages inside the call screen, kept afterwards.'],
      ['Move a call between your devices', 'Answer on the laptop, carry on from the phone.'],
      ['Honest outcomes', 'Declined, not answered, missed while busy, cancelled and failed are different things, and the log says which.'],
    ],
  },
  {
    id: 'groupcalls',
    title: 'Group calls',
    blurb: 'The whole group, ringing at once.',
    items: [
      ['Group voice and video', 'Call every member of a group together.'],
      ['Ring the group', 'Everyone gets a real ringing invitation; declines are tracked.'],
      ['Join one already running', '“3 members in call” — tap and drop in.'],
      ['Re-invite', 'Ring someone again who missed it.'],
      ['Away tiles', 'Someone who backgrounds the app gets a blurred Away tile instead of a frozen frame.'],
      ['Chat beside the video', 'A layout that puts the tiles on one half and a full chat panel on the other.'],
      ['Per-call statistics', 'Connection quality for a group call, not just a one-to-one.'],
      ['Leaving is not ending', 'You can drop out without hanging up on everyone else.'],
    ],
  },
  {
    id: 'groups',
    title: 'Groups',
    blurb: 'With real administration, not just a shared thread.',
    items: [
      ['Create a group', 'Name it, pick people, go.'],
      ['Name, description and photo', 'Changes are announced in the conversation.'],
      ['Member collage avatars', 'A group without a photo shows its members.'],
      ['Creator and admin badges', 'It is visible who runs the room.'],
      ['Granular permissions', 'Who can edit settings, who can send messages, who can add people.'],
      ['Join approval', 'Require an admin to approve anyone joining, with a pending queue.'],
      ['Approve everyone at once', 'Turning approval off clears the queue in one action.'],
      ['Private invitations', 'If someone’s privacy settings stop them being added, an admin can invite them instead.'],
      ['Remove members', 'And a record of it in the chat.'],
      ['Leave or delete', 'Leave the group, or delete it and its media from your device.'],
      ['System events', 'Joins, leaves, invites, renames and creation are all written into the history.'],
      ['Groups in common', 'On a profile, the groups you both belong to.'],
      ['Turn a chat into a group', 'Add people to a one-to-one and it becomes one.'],
    ],
  },
  {
    id: 'vibes',
    title: 'Vibes',
    blurb: 'A second product living inside the first: a video feed, text posts, friends, moderation and analytics.',
    items: [
      ['Two feeds', 'For You, and a Friends-only feed.'],
      ['Full-screen video reels', 'Vertical, looping, swipeable.'],
      ['Photo and video posts', 'With captions.'],
      ['Text posts', 'Styled type, no media needed.'],
      ['Link posts', 'A link as a proper card that opens in the in-app browser.'],
      ['Add music', 'A searchable Creative-Commons library, by category or free text.'],
      ['Mix your own audio', 'Balance the original video sound against the track you added.'],
      ['A volume dial', 'For the feed and for the reels player, separately.'],
      ['Creation tools', 'Text, filters, stickers, audio and volume in one editor.'],
      ['Adaptive streaming', 'Every uploaded video is transcoded to 240p, 480p and 720p so it starts fast anywhere.'],
      ['Automatic tagging', 'Uploads are tagged by AI so they can be found.'],
      ['Search and trending', 'Find videos and see what is moving.'],
      ['Saved vibes', 'Keep a post to come back to.'],
      ['Friend requests', 'Send, accept, decline, cancel, unfriend — with a three-tab manager.'],
      ['Likes and comments', 'Comment, edit your comment, delete it, see who liked.'],
      ['Share a post', 'Into a chat, into a group, to Note to Self, or repost it as your own.'],
      ['Who shared it', 'The full list, not just a count.'],
      ['A notifications inbox', 'Likes, comments, shares, friend requests, acceptances.'],
      ['Check in or say you are travelling', 'Attach a place, or a from-and-to, with map search.'],
      ['Age gate', 'Birthday and gender asked once; under-age accounts are blocked.'],
      ['Automatic screening', 'Uploads are checked before publication and blocked with a stated reason.'],
      ['Reporting', 'Four categories, and they go somewhere.'],
      ['Published strike thresholds', 'Ten strikes is a week, twenty is thirty days, thirty is permanent. Written down rather than arbitrary.'],
      ['Your account status', 'Your own strike count, risk level and open reports, visible to you.'],
      ['Feed controls', 'Hide a post, ask for fewer like it, or block whoever made it.'],
      ['Post insights', 'Views, impressions, accounts reached, engagement rate.'],
      ['Audience insights', 'Gender split, age range and top locations of your viewers, once there is enough data to be meaningful.'],
    ],
  },
  {
    id: 'pages',
    title: 'Pages, profiles and AI channels',
    blurb: '',
    items: [
      ['Pages', 'A business or creator identity separate from your personal account, built in a four-step wizard.'],
      ['Twelve categories', 'And the wizard’s questions change depending on which you pick.'],
      ['A real page profile', 'Cover, avatar, category, followers, an info card with website, address, hours and phone, and a grid of posts.'],
      ['Follow pages', 'With live follower counts.'],
      ['Post as your page', 'Choose which identity a Vibe goes out under.'],
      ['Four AI channels', 'Deep Space Signal, The Mind Lab, Tomorrow’s Tech and Blue Planet Files — read-only channels where five AI personas hold an ongoing conversation you can follow.'],
      ['Subscribe, mute, react', 'One emoji per message, and a live count of who else is reading.'],
      ['An AI assistant', 'A Gemini-backed assistant with live web search.'],
      ['Profile with @username', 'Photo, name, unique handle. Changeable twice a month.'],
      ['An About line', 'With its own audience control.'],
      ['Your QR code', 'A personal code that starts a chat with you. Save it, share it, or scan someone else’s — including from a saved image.'],
      ['Contact sync', 'Find which of your contacts are already here.'],
    ],
  },
  {
    id: 'security',
    title: 'Security and privacy',
    blurb: 'The part that is not a feature list so much as the reason the rest exists.',
    items: [
      ['End-to-end encryption', 'Messages are sealed on your device and can only be opened on the recipient’s.'],
      ['Sealed per device', 'Each device you own gets its own sealed copy, so linking a new one does not lock it out — and does not hand the server a key.'],
      ['Encrypted media and locations', 'Not just the text.'],
      ['Works while the phone is locked', 'Notifications can still be decrypted with the app closed.'],
      ['A 60-digit security code', 'Compare it by QR or by reading it aloud, and both sides see that the other verified. The browser and the phone compute the same code from the same keys.'],
      ['Disappearing messages', 'New messages vanish for everyone after 3 hours, 12 hours, 1 day or 15 days — and turning it on is announced, not silent.'],
      ['Lock a chat with a PIN', 'Always prompt, or after a delay you choose.'],
      ['Face ID or fingerprint', 'With a PIN fallback after two failures.'],
      ['Private Media', 'Photos and videos stay covered until you unlock them; the text stays readable.'],
      ['Login alerts you keep', 'Every approved login writes a permanent encrypted record into your Note to Self, with the device and where it was.'],
      ['Active sessions', 'Every logged-in device, when it was last used and where — and a button to end any of them.'],
      ['Trusted devices', 'Only a device you trust can log the others out.'],
      ['Blocking', 'They cannot message or call you, and there is a screen to manage the list.'],
      ['Last seen and online control', 'Everyone, contacts, nobody, or everyone-except-these — with per-chat overrides.'],
      ['The reciprocity rule', 'Hide your last seen and you stop seeing everyone else’s. It costs something, on purpose.'],
      ['Read receipt control', 'Globally or per chat, and it works both ways too.'],
      ['Who can add you to groups', 'With an exceptions list.'],
      ['Strong password rules', 'And email verification by six-digit code.'],
    ],
  },
  {
    id: 'guest',
    title: 'Temporary guest links',
    blurb: 'Talk to someone who will never install anything.',
    items: [
      ['Chat with someone not on Allo', 'Create a link, send it, and they answer in a browser. No account, no download.'],
      ['You choose how long', 'One, three, seven or fifteen days. Fifteen is the ceiling.'],
      ['Optional passcode', 'Hashed on the server, never checked on the guest’s device.'],
      ['One link, one person', 'The first browser to open it claims it. Forwarding the link cannot hand your conversation to a stranger.'],
      ['Label your links', 'So a list of links is not a list of tokens.'],
      ['End it instantly', 'Revoking kills the link and deletes the conversation.'],
      ['A record that outlives the chat', 'The person stays in your list — who they were, when you talked — after the conversation is gone.'],
      ['Live status', 'Whether they have opened it yet, and how long is left.'],
      ['Real limits, stated', 'A temporary guest cannot be added to groups, and Vibes posts cannot be shared into a temporary chat.'],
      ['A full guest experience', 'Their browser gets media, documents, voice notes, replies, polls they can vote in, link cards, a media gallery and a send queue that survives losing signal.'],
    ],
  },
  {
    id: 'web',
    title: 'Allo for Web and multiple devices',
    blurb: '',
    items: [
      ['The whole thing in a browser', 'Chats, calls, Vibes, media, polls, profiles.'],
      ['Sign in by QR', 'Scan with your phone.'],
      ['Approve from your other device', 'A new device waits for one you already trust.'],
      ['Email-code fallback', 'For when no other device is to hand.'],
      ['History that follows you', 'Your existing phone re-encrypts its history specifically for the new device and uploads that. Nobody in between can read it — including the server.'],
      ['Actions sync', 'Delete, answer or decline on one device and the others agree.'],
      ['Linked device manager', 'See them all, end any of them.'],
      ['iPad supported', 'The iOS app runs on tablets.'],
    ],
  },
  {
    id: 'look',
    title: 'Making it yours',
    blurb: 'More of the code is about this than you would guess.',
    items: [
      ['Light, dark or system', 'It follows your phone if you let it.'],
      ['Eight chat themes', 'Allo, Ocean, Mint, Sage, Sand, Blush, Lavender and Slate — a wallpaper and the bubble that suits it, each drawn twice so it is right in both light and dark.'],
      ['The Allo doodle', 'About a hundred and sixty little line drawings, edge to edge, tinted to the theme. On or off with one tap.'],
      ['Swipe to choose', 'The whole chat previews each theme — swipe, see it at night too, tap to apply.'],
      ['Per-chat themes', 'One conversation can look different from the rest, and only you see it.'],
      ['Ten app icons', 'Default, Neon Dreams, Luxury Gold, Midnight, Clean White, Ocean Blue, Crimson, Forest, Royal and Retro 80s. Nine on iPhone — Neon Dreams is Android-only.'],
      ['Message text size', 'Bigger, if bigger is better.'],
      ['Reset everything', 'One tap back to default.'],
    ],
  },
  {
    id: 'backup',
    title: 'Backup and getting your data out',
    blurb: '',
    items: [
      ['Encrypted cloud backup', 'To iCloud or Google Drive.'],
      ['A recovery key only you hold', 'Which means Allo cannot recover it for you. That is the trade.'],
      ['On a schedule', 'Daily, weekly, monthly or never.'],
      ['On your terms', 'Whether to use cellular data, whether to include videos.'],
      ['Quota awareness', 'Allo checks your Drive space and warns before a backup that will not fit.'],
      ['Restore on a new phone', 'Allo looks for a backup on first launch and offers it.'],
      ['Export a chat as a ZIP', 'An offline HTML transcript, a plain-text copy, and every photo, video, voice note and document in it.'],
      ['Export history', 'What you exported before, with file counts and sizes.'],
      ['Instant, offline chats', 'Messages live in a database on the device, so a conversation opens immediately and works with no signal.'],
    ],
  },
  {
    id: 'lang',
    title: 'Languages',
    blurb: '',
    items: [
      ['Ten interface languages', 'English, Spanish, French, German, Chinese, Japanese, Russian, Arabic, Portuguese and Hindi — around 1,510 strings translated in each.'],
      ['Message translation', 'Turn it on for a conversation and read what arrives.'],
      ['An honest disclaimer', 'Allo tells you a translation may be wrong. Machine translation sometimes is.'],
      ['Translations are remembered', 'Reopen the chat and nothing is re-translated.'],
      ['Notifications in the right language', 'Each device gets notifications in the language that device is set to — not the language the sender uses.'],
    ],
  },
  {
    id: 'notif',
    title: 'Notifications',
    blurb: '',
    items: [
      ['Rich previews', 'A photo, a voice message, a poll, a flight, a document, a location — each described as what it is.'],
      ['Or no preview at all', 'Name and message, name only, or neither.'],
      ['Six sounds', 'Default, Note, Chime, Pulse, Bell or None, previewed in the picker.'],
      ['Calls configured separately', 'Because they are not the same thing as a message.'],
      ['Ringtone and vibration', 'Set both.'],
      ['Repeat alerts', 'Never, or 1, 2, 3, 5 or 10 times.'],
      ['In-chat sounds', 'Silenceable.'],
      ['Mute one chat', 'Without muting everything.'],
      ['When a contact joins', 'If you want to know.'],
      ['Calls cut through', 'Incoming calls use a maximum-priority channel that bypasses Do Not Disturb; an active call shows a persistent notification.'],
    ],
  },
  {
    id: 'craft',
    title: 'Details',
    blurb: 'The things nobody asks for and everybody notices.',
    items: [
      ['Human error messages', 'One hundred and thirty-eight of them, each saying what happened and what to do — not a code.'],
      ['Every empty list says something', 'Rather than being blank.'],
      ['Offline banner', 'And a “back online” when it returns.'],
      ['Skeleton loading', 'A shape of the thing, not a white screen.'],
      ['Haptics', 'Reactions and controls answer back.'],
      ['A designed launch', 'An animated brand mark instead of a blank frame.'],
      ['Quiet hints', 'Small tips that explain a gesture once.'],
      ['Updates without the app store', 'Allo can update itself, with progress and a warning if your connection is slow.'],
    ],
  },
];

/** For the summary line under the index. */
export const FEATURE_COUNT = FEATURE_GROUPS.reduce((n, g) => n + g.items.length, 0);
